import React, { useMemo, useState } from 'react';
import { Edit, Trash2, ArrowUpDown } from 'lucide-react';

/**
 * CRMList — Monday.com-style flat table of every contact.
 *
 * All contacts across all stages are shown. Columns are sortable; the
 * stage cell is an inline `<select>` so a user can reclassify without
 * opening the edit modal.
 *
 * Props: same as CRMBoard — stages, grouped, showArchived, onEdit, onDelete,
 * onStageChange(contactId, newStageId), filterQuery.
 */
const COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'company', label: 'Company' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'stage', label: 'Stage' },
  { key: 'updated_at', label: 'Last Update' },
];

export default function CRMList({ stages, grouped, showArchived, onEdit, onDelete, onStageChange, filterQuery }) {
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');

  const stageMap = useMemo(() => Object.fromEntries(stages.map((s) => [s.id, s])), [stages]);

  const rows = useMemo(() => {
    const all = [];
    Object.entries(grouped).forEach(([stageId, list]) => {
      const s = stageMap[stageId];
      if (!s) return;
      if (!showArchived && s.is_archived) return;
      list.forEach((c) => all.push({ ...c, _stageLabel: s.label, _stageColor: s.color, _stageArchived: s.is_archived }));
    });
    const q = (filterQuery || '').toLowerCase();
    const filtered = q
      ? all.filter(
          (c) =>
            c.name?.toLowerCase().includes(q) ||
            c.email?.toLowerCase().includes(q) ||
            c.company?.toLowerCase().includes(q),
        )
      : all;
    return filtered.sort((a, b) => {
      const va = a[sortBy] ?? '';
      const vb = b[sortBy] ?? '';
      const cmp = String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [grouped, stages, stageMap, showArchived, filterQuery, sortBy, sortDir]);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  return (
    <div className="flex-1 overflow-auto p-6" data-testid="crm-list-view">
      <div className="bg-brand-surface rounded-xl border border-white/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className="text-left px-4 py-3 text-[11px] uppercase tracking-wider text-gray-400 font-semibold cursor-pointer hover:text-white"
                  onClick={() => toggleSort(c.key)}
                  data-testid={`sort-${c.key}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    <ArrowUpDown className="w-3 h-3 opacity-50" />
                    {sortBy === c.key && <span className="text-brand-gold">{sortDir === 'asc' ? '↑' : '↓'}</span>}
                  </span>
                </th>
              ))}
              <th className="text-right px-4 py-3 text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`row-${c.id}`}>
                <td className="px-4 py-3 text-white font-medium">{c.name}</td>
                <td className="px-4 py-3 text-gray-300">{c.company || '—'}</td>
                <td className="px-4 py-3 text-gray-300">{c.email || '—'}</td>
                <td className="px-4 py-3 text-gray-300">{c.phone || '—'}</td>
                <td className="px-4 py-3">
                  <select
                    value={c.stage}
                    onChange={(e) => onStageChange(c.id, e.target.value)}
                    className="px-2 py-1 rounded-md text-xs bg-white/5 border border-white/10 text-white cursor-pointer hover:border-brand-gold/50"
                    style={{ borderLeft: `3px solid ${c._stageColor}` }}
                    data-testid={`stage-select-${c.id}`}
                  >
                    {stages.map((s) => (
                      <option key={s.id} value={s.id} className="bg-brand-surface">
                        {s.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">{fmt(c.updated_at || c.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => onEdit(c)} className="p-1 text-gray-500 hover:text-brand-gold mr-1">
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => onDelete(c.id)} className="p-1 text-gray-500 hover:text-rose-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-12 text-gray-500 text-sm">
                  No contacts match. Try adding one from the Create button.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
